import { create } from "zustand";

export interface Email {
  id: string;
  folderId: string;
  from: { name: string; email: string };
  to: { name?: string; email: string }[];
  cc?: { name?: string; email: string }[];
  bcc?: { name?: string; email: string }[];
  subject: string;
  snippet: string;
  body: string;
  bodyHtml?: string;
  date: string;
  isRead: boolean;
  isFlagged: boolean;
  labels: string[];
  size?: number;
  hasAttachments?: boolean;
  attachments?: {
    filename: string;
    contentType: string;
    size: number;
    url?: string;
    contentId?: string;
    contentBase64?: string;
    isInline?: boolean;
    part?: string;
  }[];
  headers?: Record<string, string>;
}

interface EmailState {
  emails: Email[];
  currentEmail: Email | null;
  selectedEmailIds: Set<string>;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  hasMore: boolean;
  sortBy: "date" | "from" | "subject";
  sortOrder: "asc" | "desc";
  searchQuery: string;
  refreshTrigger: number;

  setEmails: (emails: Email[]) => void;
  appendEmails: (emails: Email[]) => void;
  setCurrentEmail: (email: Email | null) => void;
  toggleSelectEmail: (id: string) => void;
  selectAllEmails: () => void;
  clearSelection: () => void;
  setSelectedEmailIds: (ids: Set<string>) => void;
  markAsRead: (ids: string[]) => void;
  markAsUnread: (ids: string[]) => void;
  toggleFlag: (id: string) => void;
  removeEmails: (ids: string[]) => void;
  restoreDeletedEmails: (emails: Email[]) => void;
  moveEmails: (ids: string[], folderId: string) => void;
  addLabelToEmails: (ids: string[], labelId: string) => void;
  removeLabelFromEmails: (ids: string[], labelId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTotalCount: (count: number) => void;
  setPage: (page: number) => void;
  setHasMore: (hasMore: boolean) => void;
  setSortBy: (sort: "date" | "from" | "subject") => void;
  setSortOrder: (order: "asc" | "desc") => void;
  setSearchQuery: (query: string) => void;
  triggerRefresh: () => void;
  reset: () => void;
}

export const useEmailStore = create<EmailState>()((set, get) => ({
  emails: [],
  currentEmail: null,
  selectedEmailIds: new Set<string>(),
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  hasMore: false,
  sortBy: "date",
  sortOrder: "desc",
  searchQuery: "",
  refreshTrigger: 0,

  setEmails: (emails) => set({ emails }),
  appendEmails: (emails) =>
    set((state) => ({ emails: [...state.emails, ...emails] })),

  setCurrentEmail: (email) => set({ currentEmail: email }),

  toggleSelectEmail: (id) =>
    set((state) => {
      const next = new Set(state.selectedEmailIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedEmailIds: next };
    }),

  selectAllEmails: () =>
    set((state) => ({
      selectedEmailIds: new Set(state.emails.map((e) => e.id)),
    })),

  clearSelection: () => set({ selectedEmailIds: new Set<string>() }),

  setSelectedEmailIds: (ids) => set({ selectedEmailIds: ids }),

  markAsRead: (ids) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        ids.includes(e.id) ? { ...e, isRead: true } : e,
      ),
    })),

  markAsUnread: (ids) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        ids.includes(e.id) ? { ...e, isRead: false } : e,
      ),
    })),

  toggleFlag: (id) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        e.id === id ? { ...e, isFlagged: !e.isFlagged } : e,
      ),
      currentEmail:
        state.currentEmail?.id === id
          ? { ...state.currentEmail, isFlagged: !state.currentEmail.isFlagged }
          : state.currentEmail,
    })),

  removeEmails: (ids) =>
    set((state) => ({
      emails: state.emails.filter((e) => !ids.includes(e.id)),
      selectedEmailIds: (() => {
        const next = new Set(state.selectedEmailIds);
        ids.forEach((id) => next.delete(id));
        return next;
      })(),
      currentEmail: ids.includes(state.currentEmail?.id ?? "")
        ? null
        : state.currentEmail,
    })),

  // Restaura correos eliminados localmente (undo de "borrar"/mover a papelera).
  // Re-inserta manteniendo el orden existente y re-selecciona los restaurados.
  restoreDeletedEmails: (emails) =>
    set((state) => {
      if (emails.length === 0) return state;
      const existingIds = new Set(state.emails.map((e) => e.id));
      const restored = emails.filter((e) => !existingIds.has(e.id));
      if (restored.length === 0) return state;
      return {
        emails: [...state.emails, ...restored],
        selectedEmailIds: (() => {
          const next = new Set(state.selectedEmailIds);
          restored.forEach((e) => next.add(e.id));
          return next;
        })(),
      };
    }),

  moveEmails: (ids, folderId) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        ids.includes(e.id) ? { ...e, folderId } : e,
      ),
      selectedEmailIds: (() => {
        const next = new Set(state.selectedEmailIds);
        ids.forEach((id) => next.delete(id));
        return next;
      })(),
    })),

  addLabelToEmails: (ids, labelId) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        ids.includes(e.id) && !e.labels.includes(labelId)
          ? { ...e, labels: [...e.labels, labelId] }
          : e,
      ),
      currentEmail:
        state.currentEmail && ids.includes(state.currentEmail.id) && !state.currentEmail.labels.includes(labelId)
          ? { ...state.currentEmail, labels: [...state.currentEmail.labels, labelId] }
          : state.currentEmail,
    })),

  removeLabelFromEmails: (ids, labelId) =>
    set((state) => ({
      emails: state.emails.map((e) =>
        ids.includes(e.id)
          ? { ...e, labels: e.labels.filter((l) => l !== labelId) }
          : e,
      ),
      currentEmail:
        state.currentEmail && ids.includes(state.currentEmail.id)
          ? { ...state.currentEmail, labels: state.currentEmail.labels.filter((l) => l !== labelId) }
          : state.currentEmail,
    })),

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setTotalCount: (totalCount) => set({ totalCount }),
  setPage: (page) => set({ page }),
  setHasMore: (hasMore) => set({ hasMore }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  triggerRefresh: () => set((state) => ({ refreshTrigger: state.refreshTrigger + 1 })),

  reset: () =>
    set({
      emails: [],
      currentEmail: null,
      selectedEmailIds: new Set<string>(),
      isLoading: false,
      error: null,
      totalCount: 0,
      page: 1,
      hasMore: false,
      searchQuery: "",
    }),
}));
